import { useMemo } from 'react';
import { View, ActivityIndicator } from 'react-native';
import { MapView, type MapPin, type MapBounds } from '@/components/maps/MapView';
import { Caption, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import type { LocationCoordinates } from '@/services/location';
import { ShareLocationActionsRow } from './ShareLocationActionsRow';

/** Distinct, colorblind-friendly-ish hues for multiple live shares on one map. */
const SHARE_PIN_PALETTE = [
  '#2563EB',
  '#DC2626',
  '#059669',
  '#D97706',
  '#7C3AED',
  '#DB2777',
  '#0891B2',
  '#CA8A04',
] as const;

export type IncomingShareMapRow = {
  shareId: string;
  label: string;
  coordinate: LocationCoordinates | null;
  statusLine: string;
};

function boundsForCoordinates(coords: LocationCoordinates[]): MapBounds | null {
  if (coords.length === 0) {
    return null;
  }
  let minLat = coords[0].latitude;
  let maxLat = coords[0].latitude;
  let minLng = coords[0].longitude;
  let maxLng = coords[0].longitude;
  for (const c of coords) {
    minLat = Math.min(minLat, c.latitude);
    maxLat = Math.max(maxLat, c.latitude);
    minLng = Math.min(minLng, c.longitude);
    maxLng = Math.max(maxLng, c.longitude);
  }
  const latPad = Math.max(0.008, (maxLat - minLat) * 0.35 || 0.02);
  const lngPad = Math.max(0.008, (maxLng - minLng) * 0.35 || 0.02);
  return {
    southWest: { latitude: minLat - latPad, longitude: minLng - lngPad },
    northEast: { latitude: maxLat + latPad, longitude: maxLng + lngPad },
  };
}

type IncomingLiveSharesMapProps = {
  rows: IncomingShareMapRow[];
};

export function IncomingLiveSharesMap({ rows }: IncomingLiveSharesMapProps) {
  const theme = useTheme();

  const rowsWithColors = useMemo(
    () =>
      rows.map((row, index) => ({
        ...row,
        color: SHARE_PIN_PALETTE[index % SHARE_PIN_PALETTE.length],
      })),
    [rows],
  );

  const pins: MapPin[] = useMemo(
    () =>
      rowsWithColors
        .filter((r) => r.coordinate != null)
        .map((r) => ({
          id: `incoming-share-${r.shareId}`,
          coordinates: r.coordinate!,
          title: r.label,
          subtitle: r.statusLine,
          type: 'user' as const,
          markerColor: r.color,
        })),
    [rowsWithColors],
  );

  const bounds = useMemo(() => {
    const coords = pins.map((p) => p.coordinates);
    return boundsForCoordinates(coords);
  }, [pins]);

  const singleCenter = pins.length === 1 ? pins[0].coordinates : undefined;

  const outerStyle = useMemo(
    () => ({
      borderRadius: theme.radii.lg ?? 16,
      overflow: 'hidden' as const,
      backgroundColor: theme.colors.surfaceElevated,
    }),
    [theme],
  );

  const mapHeight = 220;

  return (
    <View style={{ gap: theme.spacing(1.25) }}>
      {pins.length > 0 ? (
        <View style={outerStyle}>
          <MapView
            pins={pins}
            bounds={bounds}
            center={pins.length === 1 ? singleCenter : undefined}
            zoomLevel={pins.length === 1 ? 14 : 11}
            showUserLocation={false}
            showIndexChips={false}
            boundsPadding={48}
            boundsAnimationDurationMs={350}
            style={{ height: mapHeight, width: '100%' }}
          />
        </View>
      ) : (
        <View
          style={[
            outerStyle,
            {
              height: mapHeight,
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing(0.75),
            },
          ]}
        >
          <ActivityIndicator size="small" color={theme.colors.primary} />
          <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', paddingHorizontal: theme.spacing(2) }}>
            Waiting for at least one shared position…
          </Caption>
        </View>
      )}

      <View style={{ gap: theme.spacing(0.75) }}>
        <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(0.25) }}>
          Key — pin colors on the map
        </Caption>
        {rowsWithColors.map((r) => (
          <View
            key={r.shareId}
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: theme.spacing(1),
            }}
          >
            <View
              style={{
                width: 12,
                height: 12,
                borderRadius: 6,
                marginTop: 4,
                backgroundColor: r.coordinate ? r.color : theme.colors.border ?? '#ccc',
                borderWidth: r.coordinate ? 0 : 1,
                borderColor: theme.colors.borderMuted ?? theme.colors.textSecondary,
              }}
            />
            <View style={{ flex: 1 }}>
              <ThemedText variant="bodyStrong">{r.label}</ThemedText>
              <Caption color={theme.colors.textSecondary}>{r.statusLine}</Caption>
              {r.coordinate ? (
                <ShareLocationActionsRow coordinate={r.coordinate} label={r.label} />
              ) : null}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}
